import React, { useState } from 'react';
import { FiSearch, FiEdit, FiTrash2, FiMapPin, FiWifi, FiWifiOff } from 'react-icons/fi';
import axios from 'axios';
import { API_URL } from '../config/api';
import EditarEquipamento from './EditarEquipamento';
import './ListarLocais.css';

const isAntenaOnline = (eq) =>
  eq.status_antena_online !== undefined && eq.status_antena_online !== null
    ? Boolean(eq.status_antena_online)
    : Boolean(Number(eq.status_internet));

const ListarLocais = ({ equipamentos, loading, onEquipamentoUpdated }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [editingEquipamento, setEditingEquipamento] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja deletar este equipamento?')) {
      return;
    }

    try {
      setDeletingId(id);
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/equipamentos/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (onEquipamentoUpdated) {
        await onEquipamentoUpdated();
      }
    } catch (error) {
      console.error('Erro ao deletar equipamento:', error);
      alert('Erro ao deletar equipamento. Tente novamente.');
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (value) => {
    if (!value) return 'N/D';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'N/D';
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredEquipamentos = equipamentos
    ? equipamentos.filter(eq => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        return (
          (eq.ip && eq.ip.toLowerCase().includes(term)) ||
          (eq.endereco && eq.endereco.toLowerCase().includes(term)) ||
          (eq.bairro && eq.bairro.toLowerCase().includes(term)) ||
          (eq.patrimonio && eq.patrimonio.toLowerCase().includes(term)) ||
          (eq.descricao_mk && eq.descricao_mk.toLowerCase().includes(term))
        );
      })
    : [];

  if (loading) {
    return (
      <div className="listar-locais">
        <div className="loading">Carregando equipamentos...</div>
      </div>
    );
  }

  return (
    <div className="listar-locais">
      <div className="locais-header">
        <div>
          <h1>Listar Locais</h1>
          <p>Gerencie todos os equipamentos cadastrados</p>
        </div>
        <div className="total-locais">
          <FiMapPin /> {equipamentos ? equipamentos.length : 0} local(is)
        </div>
      </div>

      <div className="search-container">
        <div className="search-box">
          <FiSearch className="search-icon" />
          <input
            type="text"
            className="search-input"
            placeholder="Buscar por IP, endereço, bairro, patrimônio ou descrição..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        {searchTerm && (
          <div className="search-results">
            {filteredEquipamentos.length} resultado(s) encontrado(s)
          </div>
        )}
      </div>

      {equipamentos && equipamentos.length === 0 ? (
        <div className="no-locais">
          <FiMapPin size={48} />
          <p>Nenhum equipamento cadastrado</p>
          <span>Cadastre um novo equipamento para começar</span>
        </div>
      ) : filteredEquipamentos.length === 0 ? (
        <div className="no-results">
          <FiSearch size={48} />
          <p>Nenhum resultado encontrado</p>
          <span>Tente buscar com outros termos</span>
        </div>
      ) : (
        <div className="locais-table-container">
          <table className="locais-table">
            <thead>
              <tr>
                <th>Status</th>
                <th>Descrição</th>
                <th>IP</th>
                <th>Endereço</th>
                <th>Bairro</th>
                <th>Patrimônio</th>
                <th>Última Verificação</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredEquipamentos.map((equipamento) => {
                const isOnline = isAntenaOnline(equipamento);
                return (
                  <tr key={equipamento.id}>
                    <td>
                      <div className="status-cell">
                        {isOnline ? (
                          <FiWifi className="status-icon online" />
                        ) : (
                          <FiWifiOff className="status-icon offline" />
                        )}
                        <span className={`status-text ${isOnline ? 'online' : 'offline'}`}>
                          {isOnline ? 'Online' : 'Offline'}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="descricao-cell">
                        <strong>{equipamento.descricao_mk || 'N/D'}</strong>
                      </div>
                    </td>
                    <td>
                      <span className="ip-cell">{equipamento.ip}</span>
                    </td>
                    <td>
                      <div className="endereco-cell">
                        {equipamento.endereco}{equipamento.numero ? `, ${equipamento.numero}` : ''}
                      </div>
                    </td>
                    <td>{equipamento.bairro || 'N/D'}</td>
                    <td>{equipamento.patrimonio || 'N/D'}</td>
                    <td>{formatDate(equipamento.ultima_verificacao)}</td>
                    <td>
                      <div className="actions-cell">
                        <button
                          className="action-btn edit-btn"
                          onClick={() => setEditingEquipamento(equipamento)}
                          title="Editar"
                        >
                          <FiEdit />
                        </button>
                        <button
                          className="action-btn delete-btn"
                          onClick={() => handleDelete(equipamento.id)}
                          disabled={deletingId === equipamento.id}
                          title="Deletar"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editingEquipamento && (
        <EditarEquipamento
          equipamento={editingEquipamento}
          onClose={() => setEditingEquipamento(null)}
          onEquipamentoUpdated={() => {
            setEditingEquipamento(null);
            if (onEquipamentoUpdated) {
              onEquipamentoUpdated();
            }
          }}
        />
      )}
    </div>
  );
};

export default ListarLocais;
